import { cn } from "./cn";

export type AvatarSize = "sm" | "md" | "lg" | "xl";

const SIZES: Record<AvatarSize, string> = {
  sm: "size-7 text-[11px]",
  md: "size-9 text-[13px]",
  lg: "size-16 text-lg",
  xl: "size-24 text-2xl",
};

export interface AvatarProps {
  /** A `data:` URL, or null to fall back to the initials. */
  src?: string | null;
  /** Shown when there is no picture. */
  initials: string;
  size?: AvatarSize;
  className?: string;
}

/**
 * A person, at a glance.
 *
 * The initials are not a placeholder for a missing picture so much as an equal
 * alternative — most accounts here never set one, and a ring of initials reads
 * as a deliberate identity where a generic silhouette reads as a broken image.
 */
export function Avatar({ src, initials, size = "md", className }: AvatarProps) {
  const shared = cn(
    "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full",
    SIZES[size],
    className,
  );

  if (!src) {
    return (
      <span
        aria-hidden="true"
        className={cn(shared, "bg-accent-soft font-semibold tracking-tight text-accent-strong")}
      >
        {initials}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt=""
      // A stored avatar can be any of four formats and one of them may be
      // corrupt; falling back to a plain box beats a broken-image glyph in the
      // header of every page.
      onError={(event) => {
        event.currentTarget.style.visibility = "hidden";
      }}
      className={cn(shared, "border border-line object-cover")}
    />
  );
}
